import React, { useEffect, useState } from "react";
import moment from "moment";
import numeral from "numeral";
import _ from "lodash";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { bindActionCreators, compose } from "redux";
import numberFormatter from "utils/numberFormatter";
import { ModuleBase, DefaultModuleHeader } from "@loanbossinc/component-library";
import { CardPageLayout } from "component-lib/CardPageLayout";
import { CustomizableHeroCard } from "component-lib/CustomizableHeroCard";
import Divider from "@material-ui/core/Divider";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import CircularProgress from "@material-ui/core/CircularProgress";
import { authorizedRequest } from "utils/api";
import { ToastError } from "components/Global/Toasts/Toasty";

import { dealGet } from "../../deal/deal.store";
import { actionCreators } from "../dashboard/dashboard.store";
import { StyledBodyWrapper } from "../dashboard/styles";

import {
  StyledHeader,
  StyledTotalButton,
  StyledText,
  StyledExpenseWrapper,
  StyledHeaderText,
  StyledHeaderTotal,
  StyledExpensItem,
  StyledItem,
  StyledTotal,
  StyledStatusWrapper,
  StyledNetIncomeWrapper,
  StyledDebtServiceWrapper,
  StyledServiceItem,
  StyledServiceName,
  StyledServiceTotal,
  StyledServiceMessage,
  StyledStatus,
  StyledStatusHeader,
  StyledStatusBody,
  StyledStatusTotal,
  StyledStatusName,
  StyledStatusReq,
  StyledCardContent,
  StyledCardTitle,
  StyledCardReport,
  StyledReportLabel,
  StyledReportDate,
  MultiLineItemHeader,
  MultiLineItemHeaderName,
  MultiLineItemHeaderValue
} from "./style";

const definePropertiesForMultiComparisonLI = (groups, operator, calculationType) => {
  if (groups && groups.some(group => !group.fields)) {
    return [];
  }
  if (calculationType === "cumulative") {
    const groupSums = groups.map(group => group.fields.reduce((accum, field) => accum + field.field?.value, 0));
    let selectedGroupIndex = 0;
    if (operator === "greaterOf") {
      selectedGroupIndex = groupSums.indexOf(Math.max(...groupSums));
    } else {
      selectedGroupIndex = groupSums.indexOf(Math.min(...groupSums));
    }

    return groups[selectedGroupIndex].fields;
  }
  const result = [];
  groups[0].fields.forEach((_field, idx) => {
    const fieldsFromGroups = groups.map(group => group.fields[idx].field?.value);
    let selectedGroupIndex = 0;
    if (operator === "greaterOf") {
      selectedGroupIndex = fieldsFromGroups.indexOf(Math.max(...fieldsFromGroups));
    } else {
      selectedGroupIndex = fieldsFromGroups.indexOf(Math.min(...fieldsFromGroups));
    }
    result.push(groups[selectedGroupIndex].fields[idx]);
  });

  return result;
};

const getFrequencyConversion = (frequency)=> {
  switch (frequency) {
      case "annually":
          return "annual";
      case "semi-annually":
          return "semi-annual";
      case "quarterly":
          return "quarterly";
      case "monthly":
          return "monthly";
      case "bi-monthly":
          return "bi-monthly";
      default:
          return frequency;
  }
};

const LenderReportsPreview = props => {
  const {
    deal,
    history,
    match: {
      params: { dealId, type, reportId, rgiId, dealName }
    },
    getRgiById,
    rgiForPreview,
    isRgiLoading,
    getReportGroupById,
    selectedReportGroup,
    setDashboardSelections
  } = props;

  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!rgiForPreview) {
      getRgiById({ rgiId });
    }
    if (!selectedReportGroup) {
      getReportGroupById({ reportGroupId: reportId });
    }
    if (!deal) {
      props.dealGet({ dealId });
    }

    return () => setDashboardSelections({ rgiForPreview: null });
  }, [reportId, rgiId, dealId]);

  const onReportDownload = async () => {
    setIsDownloading(true);

    const response = await authorizedRequest(`/report-groups/instances/createExcelDocuments/${rgiId}/${type}/${dealId}/${encodeURIComponent(dealName)}`, "GET", null, null, true);

    if (response.url) {
      const link = document.createElement("a");
      link.href = response.url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      ToastError("Unable to generate the report.");
    }

    setIsDownloading(false);
  };

  const isMultipleProperties = deal && deal.properties && deal.properties.length > 1;

  const noiReportComponent =
    rgiForPreview && rgiForPreview.reportComponentInstances && rgiForPreview.reportComponentInstances.find(rci => rci.type === "NOI")?.data;
  const dscrOrDYReportComponent =
    rgiForPreview && rgiForPreview.reportComponentInstances && rgiForPreview.reportComponentInstances.find(rci => rci.type === type)?.data;
  const dyReportComponent =

    rgiForPreview && rgiForPreview.reportComponentInstances && rgiForPreview.reportComponentInstances.find(rci => rci.type === "DebtYield")?.data;
  let dyIsOutstandingDebt = true;
  if(dyReportComponent?.loans && dyReportComponent?.loans.length > 0){
    const outstandingDebtSelected = dyReportComponent?.loans[0].lineItem?.outstandingDebtSelected;
    dyIsOutstandingDebt = outstandingDebtSelected !== false;
  }
  const reportInstance = rgiForPreview && rgiForPreview.reportInstances && rgiForPreview.reportInstances.find(ri => ri.type === type);

  const downloadButton = {
    primary: "true",
    style: { backgroundColor: "#005AA9" },
    label: isDownloading ? "Downloading..." : "Download Report",
    onClick: onReportDownload,
    disabled: isDownloading
  };

  const heroCard = () => (
    <div>
      <CustomizableHeroCard
        {...props}
        title={deal && deal.name}
        backLabel="All reports"
        buttons={[downloadButton]}
        cardContent={
          <StyledCardContent>
            <StyledCardTitle>{selectedReportGroup && selectedReportGroup.reportName}</StyledCardTitle>
            <StyledCardReport>
              <StyledReportLabel>Report generated on:</StyledReportLabel>
              <StyledReportDate>{rgiForPreview && moment.utc(rgiForPreview.updatedAt).format("MM/DD/YY")}</StyledReportDate>
            </StyledCardReport>
            <StyledCardReport>
              <StyledReportLabel>Data as of:</StyledReportLabel>
              <StyledReportDate>{rgiForPreview && moment.utc(rgiForPreview.asOfDate).format("MM/DD/YY")}</StyledReportDate>
            </StyledCardReport>
            <StyledCardReport>
              <StyledReportLabel>Published:</StyledReportLabel>
              <StyledReportDate>{selectedReportGroup && selectedReportGroup.frequency}</StyledReportDate>
            </StyledCardReport>
            <StyledCardReport>
              <StyledReportLabel>Started on:</StyledReportLabel>
              <StyledReportDate>{selectedReportGroup && moment.utc(selectedReportGroup.firstReportDueDate).format("MM/DD/YY")}</StyledReportDate>
            </StyledCardReport>
          </StyledCardContent>
        }
        onBackButtonClick={() => history.push(`/deal/${dealId}/lender-reports/${reportId}`)}
      />
    </div>
  );

  const getButton = total => (
    <StyledTotalButton>
      <StyledText>{numberFormatter.formatMoney(total)}</StyledText>
    </StyledTotalButton>
  );

  const RenderNoiGroup = ({ lineitems, title, total }) => {
    const sortedLineItems = _.orderBy(lineitems, "sortOrder", "asc");
    return (
    <StyledExpenseWrapper>
      <StyledHeader>
        <StyledHeaderText>{title}</StyledHeaderText>
        <StyledHeaderTotal>{numberFormatter.formatMoney(total)}</StyledHeaderTotal>
      </StyledHeader>
      <Divider />
      {isMultipleProperties &&
        sortedLineItems &&
        sortedLineItems.map(item => (
          <div key={item.id}>
            <MultiLineItemHeader>
              <MultiLineItemHeaderName>
                {`${item.name} ${item.comparisonCalculationType ? `(${item.comparisonCalculationType} comparison)` : ""}`}
              </MultiLineItemHeaderName>
              {(item.operator === "addition" && item.value >= 0) && (
                <MultiLineItemHeaderValue>{numberFormatter.formatMoney(item.value)}</MultiLineItemHeaderValue> )}
              {(item.operator === "addition" && item.value < 0) && (
                <MultiLineItemHeaderValue>{`(${numberFormatter.formatMoney(Math.abs(item.value))})`}</MultiLineItemHeaderValue>
              )}
              {(item.operator === "subtraction" && item.value <= 0) && (
                <MultiLineItemHeaderValue>{numberFormatter.formatMoney(Math.abs(item.value))}</MultiLineItemHeaderValue> )}
              {(item.operator === "subtraction" && item.value > 0) && (
                <MultiLineItemHeaderValue>{`(${numberFormatter.formatMoney(Math.abs(item.value))})`}</MultiLineItemHeaderValue>
              )}
            </MultiLineItemHeader>
            {!item.comparisonCalculationType &&
              item.fields &&
              item.fields.map(field => (
                <StyledExpensItem key={field.id} style={{ marginLeft: "3%", width: "97%" }}>
                  <StyledItem style={{ lineHeight: "10px" }}>{field.propertyName}</StyledItem>
                 {(item.operator === "addition" && item.value >= 0) && (
                    <StyledTotal sStyledExpensItemtyle={{ lineHeight: "10px" }}>{numberFormatter.formatMoney(field.field?.value)}</StyledTotal> )}
                  {(item.operator === "addition" && item.value < 0) && (
                    <StyledTotal sStyledExpensItemtyle={{ lineHeight: "10px" }}>{`(${numberFormatter.formatMoney(Math.abs(field.field?.value))})`}</StyledTotal>
                  )}
                  {(item.operator === "subtraction" && item.value <= 0) && (
                    <StyledTotal sStyledExpensItemtyle={{ lineHeight: "10px" }}>{numberFormatter.formatMoney(Math.abs(field.field?.value))}</StyledTotal> )}
                  {(item.operator === "subtraction" && item.value > 0) && (
                    <StyledTotal sStyledExpensItemtyle={{ lineHeight: "10px" }}>{`(${numberFormatter.formatMoney(Math.abs(field.field?.value))})`}</StyledTotal>
                  )}
                </StyledExpensItem>
              ))}
            {item.comparisonCalculationType &&
              item.comparisonGroups &&
              definePropertiesForMultiComparisonLI(item.comparisonGroups, item.comparisonOperator, item.comparisonCalculationType).map(field => (
                <StyledExpensItem key={field.id} style={{ marginLeft: "3%", width: "97%" }}>
                  <StyledItem style={{ lineHeight: "10px" }}>{field.propertyName}</StyledItem>
                  {(item.operator === "addition" && item.value >= 0) && (
                    <StyledTotal style={{ lineHeight: "10px" }}>{numberFormatter.formatMoney(field.field?.value)}</StyledTotal> )}
                  {(item.operator === "addition" && item.value < 0) && (
                    <StyledTotal style={{ lineHeight: "10px" }}>{`(${numberFormatter.formatMoney(Math.abs(field.field?.value))})`}</StyledTotal>
                  )}
                  {(item.operator === "subtraction" && item.value <= 0) && (
                    <StyledTotal style={{ lineHeight: "10px" }}>{numberFormatter.formatMoney(Math.abs(field.field?.value))}</StyledTotal> )}
                  {(item.operator === "subtraction" && item.value > 0) && (
                    <StyledTotal style={{ lineHeight: "10px" }}>{`(${numberFormatter.formatMoney(Math.abs(field.field?.value))})`}</StyledTotal>
                  )}
                </StyledExpensItem>
              ))}
          </div>
        ))}
      {!isMultipleProperties &&
        sortedLineItems &&
        sortedLineItems.map(item => (
          <StyledExpensItem>
            <StyledItem>{item.name}</StyledItem>
            {(item.operator === "addition" && item.value >= 0) && (
              <StyledTotal>{numberFormatter.formatMoney(item.value)}</StyledTotal> )}
            {(item.operator === "addition" && item.value < 0) && (
              <StyledTotal>{`(${numberFormatter.formatMoney(Math.abs(item.value))})`}</StyledTotal>
            )}
            {(item.operator === "subtraction" && item.value <= 0) && (
              <StyledTotal>{numberFormatter.formatMoney(Math.abs(item.value))}</StyledTotal> )}
            {(item.operator === "subtraction" && item.value > 0) && (
              <StyledTotal>{`(${numberFormatter.formatMoney(Math.abs(item.value))})`}</StyledTotal>
            )}
          </StyledExpensItem>
        ))}
    </StyledExpenseWrapper>
    );
  };

  const RenderNoi = () => (
    <StyledBodyWrapper>
      {noiReportComponent.incomeLineItems?.length ? (
        <RenderNoiGroup lineitems={noiReportComponent.incomeLineItems} title="Adjusted Income" total={noiReportComponent.income} />
      ) : null}
      {noiReportComponent.expenseLineItems?.length ? (
        <RenderNoiGroup lineitems={noiReportComponent.expenseLineItems} title="Adjusted Expenses" total={noiReportComponent.expenses} />
      ) : null}
    </StyledBodyWrapper>
  );

  const body = () => {
    if (isRgiLoading) {
      return (
        <StyledBodyWrapper style={{ alignItems: "center" }}>
          <CircularProgress size={40} style={{ color: "#005AA9" }} />
        </StyledBodyWrapper>
      );
    }

    if (!rgiForPreview || !dscrOrDYReportComponent || !noiReportComponent || !reportInstance) {
      return null;
    }

    const getLoanValue = item => {
      const value = type === "DSCR" ? item.loanDscrTotal : item.loanDebtYieldTotal;

      if (!value) {
        return "--";
      }

      return numberFormatter.formatMoney(value);
    };

    const getLineItemDescription = lineItem => {
      if (!lineItem) {
        return "";
      }

      if (type === "DSCR") {
        return handleDscrType(lineItem);
      }
      if (lineItem.lineItemType === "derivedFromLB") {
        return "Derived from LoanBoss";
      }
      else if(lineItem.currentSelectedFieldType !== "file"){
        return `${lineItem.propertyName} (Calculated)`;
      }

      return `${lineItem.sourceName} ${lineItem.fileTypeName}, uploaded on ${moment.utc(lineItem.fileCreatedAt).format("MMMM DD, YYYY")}`;
    };

    const handleDscrType = (lineItem) => {
      if(lineItem.dscrType === "From file"){
        let fieldValue = 0;
        if(lineItem.lineItemType === "greaterOf"){
          if(lineItem.comparisonDscrType1 === "From file"){
            fieldValue = lineItem?.parameters1[0];
          } else{
            fieldValue = lineItem?.parameters2[0];
          }
        }
        else {
          fieldValue = lineItem?.parameters[0];
        }
        const isFile = fieldValue?.lineItemType === "fromFile";
        if(isFile){
          return `${fieldValue?.sourceName} ${fieldValue?.fileTypeName}, uploaded on ${moment.utc(fieldValue?.fileCreatedAt).format("MMMM DD, YYYY")}`;
        } else {
          return `${fieldValue?.name} (${getFieldTypeLabelByFlag(fieldValue)})`;
        }
      }
      return lineItem.dscrType;
    };

    let isConditionMet;
    if (!reportInstance.baselineValue) {
      isConditionMet = null;
    } else {
      const decimalPlaces = type === "DSCR" ? 2 : 4;
      isConditionMet = Number(reportInstance.total.toFixed(decimalPlaces)) >= reportInstance.baselineValue;
    }

    const getStatusIcon = () => {
      if (isConditionMet === null) {
        return null;
      }

      return isConditionMet ? <CheckCircleIcon style={{ color: "#50C40A" }} /> : <CancelIcon style={{ color: "#C40A0A" }} />;
    };

    const getFieldTypeLabelByFlag = field => {
      let fieldTypeLabel = "Unknown";
      if (field.isCalculated) {
        fieldTypeLabel = "Calculated";
      }

      if (field.isFromFile) {
        fieldTypeLabel = "From file";
      }

      if (field.isBalanceOverride || field.isFinanceOverride) {
        fieldTypeLabel = "Cashflow override";
      }

      if (field.isHardCoded) {
        fieldTypeLabel = "Manual input";
      }

      if (field.isFromLoanBoss) {
        fieldTypeLabel = "Derived by LoanBoss";
      }

      if (field.isFromRentRoll) {
        fieldTypeLabel = "From rent roll";
      }
      return fieldTypeLabel;
    };

    const convertValue = value => {
      if (!value && value !== 0) {
        return "--";
      } else if (type === "DSCR") {
        return `${numeral(value).format("0.00", Math.round)}x`;
      } else if (type === "DebtYield") {
        return numeral(value).format("0.00%", Math.round);
      }

      return "--";
    };

    const getStatusString = () => {
      const typeStr = type === "DSCR" ? "DSCR" : "Debt Yield";
      const frequency = getFrequencyConversion(selectedReportGroup && selectedReportGroup.frequency);
      const isMet = isConditionMet ? `Meets ${frequency}` : `Does not meet ${frequency}`;

      return `${isMet} ${typeStr} Test`;
    };

    const getDyFromLBTypeLabel = () => dyIsOutstandingDebt ? "Outstanding Debt" : "Total Loan Amount";

    const getStatusModule = () => (
      <StyledStatusWrapper>
        <StyledStatus>
          <ModuleBase>
            <StyledStatusHeader>
              {getStatusIcon()}
              <StyledStatusTotal>{convertValue(reportInstance.total)}</StyledStatusTotal>
              <StyledStatusName>{type === "DSCR" ? "DSCR" : "DEBT YIELD"}</StyledStatusName>
            </StyledStatusHeader>
            {isConditionMet !== null ? (
              <>
                <Divider />
                <StyledStatusBody>
                  <StyledStatusReq>{`${convertValue(reportInstance.baselineValue)} Req.`}</StyledStatusReq>
                  <StyledStatusName>{getStatusString()}</StyledStatusName>
                </StyledStatusBody>
              </>
            ) : null}
          </ModuleBase>
        </StyledStatus>
      </StyledStatusWrapper>
    );

    return (
      <StyledBodyWrapper>
        {getStatusModule()}
        <StyledNetIncomeWrapper>
          <ModuleBase>
            <DefaultModuleHeader headerTitle="Net Operating Income" button={getButton(noiReportComponent.total)} />
            <RenderNoi />
          </ModuleBase>
        </StyledNetIncomeWrapper>
        <StyledDebtServiceWrapper>
          <ModuleBase>
            <DefaultModuleHeader headerTitle={type === "DSCR" ? "Debt Service" : getDyFromLBTypeLabel()} button={getButton(dscrOrDYReportComponent.total)} />
            {dscrOrDYReportComponent.loans &&
                dscrOrDYReportComponent.loans.map(item => (
                  <StyledExpenseWrapper>
                    <StyledServiceItem>
                      <StyledServiceName>{item.loanName}</StyledServiceName>
                      <StyledServiceTotal>{getLoanValue(item)}</StyledServiceTotal>
                    </StyledServiceItem>
                    <StyledServiceMessage>{getLineItemDescription(item.lineItem)}</StyledServiceMessage>
                  </StyledExpenseWrapper>
                ))}
          </ModuleBase>
        </StyledDebtServiceWrapper>
      </StyledBodyWrapper>
    );
  };

  return (
    <CardPageLayout {...props} isReversed bannerImageUrl={deal && deal.imageLarge} cardComponent={heroCard()} bodyComponent={body()} />
  );
};

const mapStateToProps = ({ lenderReportList, newDeal: { deal }, authentication: { user } }) => ({ deal, user, ...lenderReportList });

const mapDispatchToProps = dispatch => bindActionCreators({ dealGet, ...actionCreators }, dispatch);

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(LenderReportsPreview);
